import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Alert
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ClearIcon from '@mui/icons-material/Clear';
import UndoIcon from '@mui/icons-material/Undo';

const SignatureCanvas = ({ 
  onSignatureChange, 
  value = '', 
  disabled = false,
  required = false,
  error = false,
  helperText = ''
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState(value);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  const [strokes, setStrokes] = useState([]); // For undo functionality

  useEffect(() => {
    if (signatureMode === 'type') {
      setTypedSignature(value);
    }
  }, [value, signatureMode]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 150;

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;

    if (e.touches) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    setIsDrawing(true);
    const point = getCanvasPoint(e);
    setLastPoint(point);
    
    // Start new stroke
    const newStroke = [point];
    setStrokes(prev => [...prev, newStroke]);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentPoint = getCanvasPoint(e);

    if (lastPoint) {
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.stroke();

      // Add point to current stroke
      setStrokes(prev => {
        const newStrokes = [...prev];
        newStrokes[newStrokes.length - 1].push(currentPoint);
        return newStrokes;
      });
    }

    setLastPoint(currentPoint);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    setLastPoint(null);
    setHasDrawnSignature(true);
    
    // Convert canvas to base64 and notify parent
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onSignatureChange(signatureData, 'drawn');
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    setHasDrawnSignature(false);
    setStrokes([]);
    onSignatureChange('', 'drawn');
  };

  const undoLastStroke = () => {
    if (strokes.length === 0) return;

    const newStrokes = strokes.slice(0, -1);
    setStrokes(newStrokes);

    // Redraw canvas
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Redraw all remaining strokes
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    newStrokes.forEach(stroke => {
      if (stroke.length > 1) {
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x, stroke[i].y);
        }
        ctx.stroke();
      }
    });

    setHasDrawnSignature(newStrokes.length > 0);
    
    if (newStrokes.length > 0) {
      const signatureData = canvas.toDataURL('image/png');
      onSignatureChange(signatureData, 'drawn');
    } else {
      onSignatureChange('', 'drawn');
    }
  };

  const handleTypedSignatureChange = (e) => {
    const newValue = e.target.value;
    setTypedSignature(newValue);
    onSignatureChange(newValue, 'typed');
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSignatureMode(newMode);
      
      if (newMode === 'type') {
        onSignatureChange(typedSignature, 'typed');
      } else {
        if (hasDrawnSignature) {
          const canvas = canvasRef.current;
          const signatureData = canvas.toDataURL('image/png');
          onSignatureChange(signatureData, 'drawn');
        } else {
          onSignatureChange('', 'drawn');
        }
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Signature {required && '*'}
        </Typography>
        
        <ToggleButtonGroup
          value={signatureMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="draw">
            <DrawIcon sx={{ mr: 1 }} />
            Draw
          </ToggleButton>
          <ToggleButton value="type">
            <KeyboardIcon sx={{ mr: 1 }} />
            Type
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {signatureMode === 'draw' ? (
        <Box>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
              border: error ? '2px solid' : '1px solid',
              borderColor: error ? 'error.main' : 'divider'
            }}
          >
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Draw your signature in the box below:
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <canvas
                ref={canvasRef}
                style={{
                  width: '100%',
                  height: '150px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: disabled ? 'not-allowed' : 'crosshair',
                  touchAction: 'none'
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              
              {!hasDrawnSignature && !disabled && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    color: '#aaa',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                >
                  Sign here with your mouse or finger
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearCanvas}
                disabled={disabled || !hasDrawnSignature}
                variant="outlined"
              >
                Clear
              </Button>
              <Button
                size="small"
                startIcon={<UndoIcon />}
                onClick={undoLastStroke}
                disabled={disabled || strokes.length === 0}
                variant="outlined"
              >
                Undo
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (
        <TextField
          fullWidth
          placeholder="Type your full name as your signature"
          value={typedSignature}
          onChange={handleTypedSignatureChange}
          disabled={disabled}
          error={error}
          variant="outlined"
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'cursive',
              fontSize: '18px'
            }
          }}
        />
      )}

      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {error && !helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {signatureMode === 'draw' ? 'Please provide your signature' : 'Please type your signature'}
        </Typography>
      )}
    </Box>
  );
};

export default SignatureCanvas;